"use client";
import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import profoundLogo from "../images/profound-logo.svg";
import profoundWord from "../images/profound-word.svg";
import {
  checkMicrophonePermission,
  verifyMicrophoneAccess,
  monitorMicrophonePermission,
  type MicrophonePermissionStatus,
} from "../lib/microphone-permissions";
import { TransitionLoadingOverlay } from "../features/AgentPage/Loading/TransitionLoadingOverlay";
import { useSignedUrl } from "../hooks/useSignedUrl";
import { useABTestContext } from "../providers/ABTestProvider";

interface UserData {
  name: string;
  phone: string;
  linkedinUrl: string;
  linkedinData: any;
  linkedApiData?: string | null;
}

interface ProfoundAudioCallProps {
  onEndCall?: () => void | Promise<void>;
  userData: UserData | null;
  linkedinLoading: boolean;
  linkedinUserData: any;
  onTransitionComplete?: () => void;
}

export default function ProfoundAudioCall({
  onEndCall,
  userData,
  linkedinLoading,
  linkedinUserData,
  onTransitionComplete,
}: ProfoundAudioCallProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get A/B test variant from context
  const { variant } = useABTestContext();

  // Get UTM parameters from URL
  const utmParams = {
    utm_source: searchParams.get("utm_source"),
    utm_medium: searchParams.get("utm_medium"),
    utm_campaign: searchParams.get("utm_campaign"),
    utm_term: searchParams.get("utm_term"),
    utm_content: searchParams.get("utm_content"),
  };

  // Pass v and UTM parameters to signed URL API
  const { signedUrl } = useSignedUrl(true, {
    v: variant || undefined,
    utm_source: utmParams.utm_source || undefined,
    utm_medium: utmParams.utm_medium || undefined,
    utm_campaign: utmParams.utm_campaign || undefined,
    utm_term: utmParams.utm_term || undefined,
    utm_content: utmParams.utm_content || undefined,
  });
  const [isListening, setIsListening] = useState(true);
  const [activeWave, setActiveWave] = useState(4);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isCallEnded] = useState(false);
  const [_error, setError] = useState<string | null>(null);
  const [buttonStatus, setButtonStatus] = useState<string>("START CONVERSATION WITH PROFOUND");
  const [_isUserSpeaking, setIsUserSpeaking] = useState(false);
  const [_micPermissionStatus, setMicPermissionStatus] = useState<MicrophonePermissionStatus | null>(
    null
  );
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);
  const conversationRef = useRef<any>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const redirectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Check and monitor microphone permission
  useEffect(() => {
    const checkPermission = async () => {
      const status = await checkMicrophonePermission();
      setMicPermissionStatus(status);
    };

    checkPermission();

    // Monitor permission changes
    const stopMonitoring = monitorMicrophonePermission((status) => {
      setMicPermissionStatus(status);
    });

    return () => {
      stopMonitoring();
    };
  }, []);

  // Simulate voice activity animation
  useEffect(() => {
    if (isListening && isConnected) {
      const interval = setInterval(() => {
        setActiveWave(Math.floor(Math.random() * 9));
      }, 500);
      return () => clearInterval(interval);
    }
  }, [isListening, isConnected]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
        redirectTimeoutRef.current = null;
      }
    };
  }, []);

  const initializeAgent = async () => {
    if (!userData) return;

    setIsConnecting(true);
    setError(null);
    setButtonStatus("WAITING FOR MICROPHONE ACCESS");

    // Check permission status first
    const permissionStatus = await checkMicrophonePermission();
    setMicPermissionStatus(permissionStatus);

    if (permissionStatus === "denied") {
      setError("Microphone access has been denied. Please enable it in your browser settings.");
      setButtonStatus("Microphone access denied");
      setIsConnecting(false);
      return;
    }

    if (permissionStatus === "prompt") {
      setButtonStatus("WAITING FOR MICROPHONE ACCESS");
    } else if (permissionStatus === "granted") {
      setButtonStatus("Profound is connecting...");
    }

    try {
      // First, request microphone permission explicitly and set up audio monitoring
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      // Verify microphone access
      if (!verifyMicrophoneAccess(stream)) {
        throw new Error("Microphone access not properly granted");
      }

      // Update permission status after successful access
      setMicPermissionStatus("granted");

      // Listen for track ending events (user may revoke permission)
      const audioTracks = stream.getAudioTracks();
      const micTrack = audioTracks[0];
      if (micTrack) {
        micTrack.addEventListener("ended", () => {
          console.log("Microphone track ended - user may have revoked permission");
          setError("Microphone access was revoked");
          setButtonStatus("WAITING FOR MICROPHONE ACCESS");
          setIsConnecting(false);
          setIsConnected(false);
          setMicPermissionStatus("denied");
        });
      }

      // Set up audio context for monitoring user speech
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      const microphone = audioContext.createMediaStreamSource(stream);

      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;
      microphone.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      // Stop the test stream immediately
      stream.getTracks().forEach((track) => track.stop());

      setButtonStatus("Profound is waking up...");

      // Create context for the agent - THIS IS WHERE WE PASS DYNAMIC VARIABLES
      // The linkedinData comes from RelevanceAI API

      const agentContext = {
        name: userData.name || "User",
        phone: userData.phone || "",
        linkedinUrl: userData.linkedinUrl || "",
        linkedin_data: JSON.stringify(userData.linkedinData || {}),
        userSummary: userData.linkedinData
          ? `${userData.name || "User"} from ${
              userData.linkedinData?.location || "Unknown location"
            }. ${userData.linkedinData?.headline || ""}`
          : `${userData.name || "User"} - Ready for AI coaching`,
        experience: JSON.stringify(userData.linkedinData?.experience || []),
        skills: Array.isArray(userData.linkedinData?.skills)
          ? userData.linkedinData.skills.join(", ")
          : JSON.stringify(userData.linkedinData?.skills || []),
        userId: JSON.parse(userData?.linkedApiData || "{}")?.user?.user_id || "",
      };

      // Fetch signed URL using SWR
      setButtonStatus("Profound is waking up...");

      setButtonStatus("Profound is joining the call...");

      // Define dynamic variables for ElevenLabs
      const dynamicVariables = {
        name: agentContext.name,
        phone: agentContext.phone,
        linkedinUrl: agentContext.linkedinUrl,
        linkedin_data: agentContext.linkedin_data,
        userSummary: agentContext.userSummary,
        location: userData.linkedinData?.location || "Unknown",
        headline: userData.linkedinData?.headline || "",
        skills: agentContext.skills,
        experience: agentContext.experience,
        userID: agentContext.userId || "",
      };

      console.log("*** DYNAMIC VARIABLES ***");
      console.log(dynamicVariables);

      // Dynamically import the Conversation class (client-side only)
      const { Conversation } = await import("@elevenlabs/client");

      // Initialize ElevenLabs Conversation with proper configuration
      // Pass dynamic variables to the conversation - these will be available as {{variable_name}}
      const conversation = await Conversation.startSession({
        signedUrl: signedUrl,
        // Client configuration for better interruption handling
        clientTools: {
          // Enable interruption detection
          // This allows the agent to be interrupted naturally
        },
        // Dynamic variables are passed here at the root level
        // These variables can be used in ElevenLabs agent prompts as {{variable_name}}
        dynamicVariables: dynamicVariables,
        onConnect: () => {
          setIsConnected(true);
          setIsConnecting(false);
          setButtonStatus("Profound is here! Start talking...");

          // Start monitoring audio levels for visual feedback
          if (analyserRef.current) {
            const monitorAudioLevel = () => {
              if (!analyserRef.current || !conversationRef.current) return;

              const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
              analyserRef.current.getByteFrequencyData(dataArray);

              // Calculate average volume
              const average = dataArray.reduce((a, b) => a + b) / dataArray.length;

              // Threshold for detecting speech
              const speechThreshold = 30;
              setIsUserSpeaking(average > speechThreshold);

              requestAnimationFrame(monitorAudioLevel);
            };
            monitorAudioLevel();
          }
        },
        onDisconnect: (reason: any) => {
          setIsConnected(false);
          setIsUserSpeaking(false);
          setButtonStatus("Profound is ending the call...");

          // Clean up audio context
          if (audioContextRef.current) {
            audioContextRef.current.close();
            audioContextRef.current = null;
          }

          // Clean up conversation
          if (conversationRef.current) {
            conversationRef.current = null;
          }

          if (reason && reason.type === "error") {
            setError(`Profound got disconnected: ${reason.message || "Unknown reason"}`);
          }

          // Show loading overlay with 4 steps, then redirect to waitlist
          setShowLoadingOverlay(true);
        },
        onError: (error: any) => {
          setError(error.message || "An error occurred during the call");
          setIsConnecting(false);
          setIsConnected(false);
          setIsUserSpeaking(false);
        },
        onModeChange: (mode: any) => {
          if (mode && mode.mode) {
            // Update status based on mode
            const modeStatus =
              mode.mode === "listening"
                ? "Profound AI is listening..."
                : mode.mode === "speaking"
                  ? "Profound AI is speaking..."
                  : mode.mode === "thinking"
                    ? "Profound AI is thinking..."
                    : `Profound AI: ${mode.mode}`;

            setButtonStatus(modeStatus);
          }
        },
        onMessage: (message: any) => {
          if (message.type === "interruption") {
            setButtonStatus("You interrupted - Profound AI is listening...");
            setIsUserSpeaking(true);
          }
        },
      });

      conversationRef.current = conversation;
    } catch (err) {
      if (err instanceof Error) {
        if (err.name === "NotAllowedError" || err.message.includes("permission")) {
          setError(
            "Profound needs microphone access to chat with you! Please allow microphone access and try again."
          );
        } else if (err.name === "NotFoundError") {
          setError("Profound can't find your microphone! Please connect one and try again.");
        } else {
          setError(`Oops! Profound ran into an issue: ${err.message}`);
        }
      } else {
        setError("Profound couldn't start the call. Please try again!");
      }
      setIsConnecting(false);
      setButtonStatus("START CONVERSATION WITH PROFOUND");
    }
  };

  const handleLoadingComplete = () => {
    // Hide loading overlay and start fade-out transition
    setShowLoadingOverlay(false);
    setIsTransitioning(true);

    // Clear any existing timeout before setting a new one
    if (redirectTimeoutRef.current) {
      clearTimeout(redirectTimeoutRef.current);
    }

    // If parent provided a transition complete handler, use that instead of default redirect
    if (onTransitionComplete) {
      onTransitionComplete();
      redirectTimeoutRef.current = null;
      return;
    }

    // Get all current query parameters (preserve any existing params)
    const currentParams = new URLSearchParams(searchParams.toString());

    // Build the waitlist URL with query params
    const waitlistUrl = `/agent/waitlist${
      currentParams.toString() ? `?${currentParams.toString()}` : ""
    }`;

    // Navigate directly to waitlist page
    // The redirect logic will check status and redirect if needed
    navigate(waitlistUrl);
    redirectTimeoutRef.current = null;
  };

  const handleEndCall = async () => {
    setIsListening(false);
    if (conversationRef.current) {
      await conversationRef.current.endSession();
      conversationRef.current = null;
    }

    // Clean up audio context
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    setIsConnected(false);
    setIsUserSpeaking(false);

    setButtonStatus("Profound is ending the call...");

    // Show loading overlay with 4 steps, then redirect to waitlist
    setShowLoadingOverlay(true);

    if (onEndCall) {
      await onEndCall();
    }
  };

  const goToProfile = () => {
    navigate("/profile");
  };

  return (
    <div
      className="min-h-screen fixed inset-0"
      style={{
        background: "linear-gradient(180deg, #F8F4C5 0%, #E4C5FF 49.52%, #F8F4C5 100%)",
      }}
    >
      {/* Loading Overlay */}
      {showLoadingOverlay && <TransitionLoadingOverlay onComplete={handleLoadingComplete} />}

      {/* Main Content */}
      <div
        className={`min-h-screen flex flex-col items-center justify-between p-4 sm:p-6 md:p-8 transition-opacity duration-700 ease-in-out ${
          showLoadingOverlay ? "opacity-0 pointer-events-none" : ""
        }`}
        style={{
          opacity: isTransitioning ? 0 : showLoadingOverlay ? 0 : 1,
        }}
      >
        {/* Logo */}
        <div className="w-full flex justify-center pt-4 sm:pt-6md:pt-8">
          <div className="flex items-center gap-1">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center relative">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-5 h-5 sm:w-6 sm:h-6 relative">
                  <img src={profoundLogo} alt="Profound Logo" className="object-contain w-full h-full" />
                </div>
              </div>
            </div>
            <div className="h-5 sm:h-6 w-auto">
              <img
                src={profoundWord}
                alt="Profound"
                width={105}
                height={22}
                className="h-full w-auto"
              />
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col items-center justify-center w-full max-w-4xl px-4">
          {/* Profound Icon */}
          <div className="mb-8 sm:mb-12 md:mb-16 flex flex-col items-center gap-4">
            <div
              className="w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 rounded-full flex items-center justify-center shadow-2xl"
              style={{ backgroundColor: "#10333B" }}
            >
              <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 relative">
                <img
                  src={profoundLogo}
                  alt="Profound Logo"
                  className="object-contain p-2 w-full h-full"
                  style={{ filter: "brightness(0) invert(1)" }}
                />
              </div>
            </div>
          </div>

          {/* Question Text */}
          <div className="text-center mb-6 sm:mb-8">
            {isConnected ? (
              buttonStatus
            ) : (
              <h1 className="px-4 text-agent-question">Welcome to Profound</h1>
            )}
          </div>

          {/* Voice Wave Indicator */}
          {!isCallEnded && (
            <div className="flex items-center justify-center gap-1 sm:gap-1.5 h-16 sm:h-20 mb-8 sm:mb-12">
              {[...Array(9)].map((_, index) => {
                const heights = ["h-2", "h-3", "h-4", "h-6", "h-8", "h-6", "h-4", "h-3", "h-2"];
                const activeHeights = [
                  "h-3",
                  "h-5",
                  "h-8",
                  "h-12",
                  "h-16",
                  "h-12",
                  "h-8",
                  "h-5",
                  "h-3",
                ];
                const isActive = Math.abs(index - activeWave) <= 1 && isListening;

                return (
                  <div
                    key={index}
                    className={`w-1 sm:w-1.5 rounded-full transition-all duration-300 ${
                      isActive ? activeHeights[index] : heights[index]
                    } ${
                      index === 4
                        ? "bg-slate-800"
                        : index === 3 || index === 5
                          ? "bg-slate-700"
                          : index === 2 || index === 6
                            ? "bg-slate-600"
                            : "bg-slate-500"
                    }`}
                  />
                );
              })}
            </div>
          )}
        </div>

        {/* Bottom Section */}
        <div className="w-full max-w-md flex flex-col items-center pb-6 sm:pb-8">
          {/* Button Controls */}
          <div className="flex gap-3 sm:gap-4 justify-center px-2 mt-2 max-w-2xl w-full mb-3 sm:mb-0 md:mb-6">
            {isCallEnded ? (
              <button
                onClick={goToProfile}
                className="text-white font-bold py-3 sm:py-3.5 md:py-4 px-6 sm:px-8 md:px-10 rounded-lg sm:rounded-xl transition-all transform hover:scale-[1.02] shadow-lg hover:shadow-xl flex items-center justify-center gap-2 text-sm sm:text-base w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none hover:opacity-90"
                style={{
                  backgroundColor: "#10333B",
                }}
              >
                <>
                  <div className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 relative">
                    <img
                      src="/images/profile-summary.svg"
                      alt="Profile Summary"
                      width={24}
                      height={24}
                      className="object-contain"
                    />
                  </div>
                  <span>GO TO PROFILE</span>
                </>
              </button>
            ) : (
              <>
                {!isConnected && !isConnecting && (
                  <button
                    onClick={initializeAgent}
                    disabled={linkedinLoading || !linkedinUserData}
                    className={`w-full bg-gray-900 hover:bg-gray-800 py-3 sm:py-4 px-6 sm:px-8 rounded-full transition-colors duration-200 flex items-center justify-center gap-2 button-text ${
                      linkedinLoading || !linkedinUserData
                        ? "bg-gray-400 cursor-not-allowed opacity-60"
                        : ""
                    }`}
                  >
                    {linkedinLoading || !linkedinUserData ? (
                      <>
                        <svg
                          className="animate-spin w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        <span>Loading LinkedIn...</span>
                      </>
                    ) : (
                      <>
                        <svg
                          className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                          />
                        </svg>
                        <span className="whitespace-nowrap">{buttonStatus}</span>
                      </>
                    )}
                  </button>
                )}

                {isConnected && (
                  <button
                    onClick={handleEndCall}
                    className="py-3 sm:py-3.5 md:py-4 px-6 sm:px-8 md:px-10 rounded-[12px] transition-all transform hover:scale-[1.02] shadow-lg hover:shadow-xl flex items-center justify-center gap-2 w-full sm:w-auto"
                    style={{
                      background: "rgba(16, 51, 59, 0.06)",
                      color: "var(--Secondary-Deep-Burgundy, #3D0B15)",
                    }}
                  >
                    <svg
                      className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                    <span className="text-center font-[Inter] text-[14px] font-bold leading-[24px] tracking-[0.28px] uppercase">
                      END CALL
                    </span>
                  </button>
                )}

                {isConnecting && (
                  <div className="w-full bg-gray-900 text-white py-3 sm:py-4 px-6 sm:px-8 rounded-full font-medium transition-colors duration-200 flex items-center justify-center gap-2 text-sm sm:text-base">
                    <svg
                      className="animate-spin w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <span className="whitespace-nowrap">
                      {isCallEnded ? "End Conversation" : "Start Conversation"}
                    </span>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Privacy Notice */}
          <p className="text-xs sm:text-sm text-gray-600 text-center px-4 max-w-lg">
            This Conversation is Private and we will never share your data
            <br className="hidden sm:block" /> with any third party platforms.
          </p>
        </div>
      </div>
    </div>
  );
}
